import { Fragment } from "react";
import { Reveal } from "./Reveal";
import { Check, Minus } from "lucide-react";
import { Link } from "@tanstack/react-router";

type Cell = string | boolean;
type Row = { label: string; hint?: string; pulse: Cell; prime: Cell; pilot: Cell };
type Group = { id: string; title: string; rows: Row[] };

const groups: Group[] = [
  {
    id: "format",
    title: "Format & upplägg",
    rows: [
      {
        label: "Affärsmodell",
        pulse: "Löpande abonnemang",
        prime: "Skräddarsydd produktion",
        pilot: "Engångsproduktion, fast pris",
      },
      {
        label: "Volym per leverans",
        pulse: "2–6+ klipp/månad",
        prime: "1 huvudfilm + cutdowns",
        pilot: "1 huvudklipp + 1 trailer",
      },
      {
        label: "Format & kanaler",
        hint: "YouTube, IG, LinkedIn, TikTok, web",
        pulse: "Alla format löpande",
        prime: "Alla format, projektbas",
        pilot: "Två format ingår",
      },
      {
        label: "Återanvändbart material",
        pulse: "Byggs upp över tid",
        prime: "Stor materialbank/projekt",
        pilot: "Begränsad bank",
      },
    ],
  },
  {
    id: "tempo",
    title: "Tempo & leverans",
    rows: [
      {
        label: "Tid från start till första film",
        pulse: "2–3 veckor",
        prime: "4–12 veckor",
        pilot: "3–5 veckor",
      },
      { label: "Leveransrytm", pulse: "Varje månad", prime: "Per milstolpe", pilot: "Engång" },
      {
        label: "Inspelningsdagar",
        pulse: "1 dag/månad (typiskt)",
        prime: "Flera dagar, planerat",
        pilot: "1 inspelningsdag",
      },
      { label: "Revideringar", pulse: "2 rundor/klipp", prime: "Avtalas i projekt", pilot: "2 rundor" },
      { label: "Akut-leveranser", pulse: "Möjligt (Core/Pro)", prime: "Planeras in", pilot: false },
    ],
  },
  {
    id: "process",
    title: "Process & team",
    rows: [
      { label: "Manus & regi", pulse: true, prime: true, pilot: true },
      { label: "Strategisk planering", pulse: "Löpande", prime: "Projektstart", pilot: "Light" },
      { label: "Full förproduktion", pulse: "Lättviktig", prime: true, pilot: "Komprimerad" },
      { label: "Grading & ljudmix", pulse: true, prime: true, pilot: true },
      { label: "Dedikerad producent", pulse: true, prime: true, pilot: true },
      { label: "Casting / skådespelare", pulse: "Vid behov", prime: true, pilot: "Vid behov" },
      { label: "Animation & grafik", pulse: "Enklare ingår", prime: "Avancerad möjlig", pilot: "Enklare ingår" },
    ],
  },
  {
    id: "ekonomi",
    title: "Ekonomi & avtal",
    rows: [
      { label: "Pris (från)", pulse: "14 995 kr/mån", prime: "150 000 kr/projekt", pilot: "28 995 kr engång" },
      { label: "Betalning", pulse: "Månadsvis", prime: "30/40/30 milstolpar", pilot: "50/50" },
      { label: "Bindningstid", pulse: "Ingen", prime: "Ingen", pilot: "Ingen" },
      { label: "Uppsägning", pulse: "Månadsvis", prime: "Ej tillämpligt", pilot: "Ej tillämpligt" },
      { label: "Kan kombineras", pulse: "Med Prime", prime: "Med Pulse", pilot: "Övergår ofta i Pulse" },
    ],
  },
  {
    id: "fit",
    title: "Bäst för",
    rows: [
      {
        label: "Typisk kund",
        pulse: "Marknadsteam med löpande behov",
        prime: "Stora kampanjer & brand",
        pilot: "Första samarbete",
      },
      {
        label: "Mognadsnivå",
        pulse: "Har en plan, vill ha tempo",
        prime: "Har en stor idé att lyfta",
        pilot: "Vill testa innan beslut",
      },
      {
        label: "Mindre lämpligt om…",
        pulse: "Ni vill ha en engångsfilm",
        prime: "Ni vill ha ständig output",
        pilot: "Ni redan vet att ni vill köra",
      },
    ],
  },
];

const columns = [
  {
    key: "pulse" as const,
    eyebrow: "Pulse",
    title: "Abonnemang",
    desc: "Löpande film, månad för månad.",
    accent: "mint",
    href: "/pulse",
    cta: "Utforska Pulse",
    highlighted: true,
  },
  {
    key: "prime" as const,
    eyebrow: "Prime",
    title: "Projekt",
    desc: "Skräddarsydd produktion när det krävs.",
    accent: "butter",
    href: "/prime",
    cta: "Utforska Prime",
    highlighted: false,
  },
  {
    key: "pilot" as const,
    eyebrow: "Pilot",
    title: "Test",
    desc: "Engångsproduktion till fast pris.",
    accent: "peach",
    href: "/kontakt",
    cta: "Boka pilot",
    highlighted: false,
  },
];

function Value({ v, accent }: { v: Cell; accent: string }) {
  if (v === true)
    return <Check className={`mx-auto h-4 w-4 text-${accent}`} aria-label="Ja" />;
  if (v === false)
    return (
      <Minus className="mx-auto h-4 w-4 text-muted-foreground/40" aria-label="Nej" />
    );
  return <span className="text-sm leading-snug text-foreground/85">{v}</span>;
}

function accentBg(a: string) {
  if (a === "mint") return "bg-mint/10 ring-mint/30 text-mint";
  if (a === "peach") return "bg-peach/10 ring-peach/30 text-peach";
  return "bg-butter/10 ring-butter/30 text-butter";
}

function accentCta(a: string) {
  if (a === "mint") return "bg-mint text-mint-foreground hover:bg-mint/90";
  if (a === "peach") return "bg-peach text-peach-foreground hover:bg-peach/90";
  return "bg-butter text-black hover:bg-butter/90";
}

export function CompareTable() {
  return (
    <Reveal>
      <div className="overflow-hidden rounded-3xl border border-white/10 bg-surface/40">
        {/* Desktop / tablet table */}
        <div className="hidden overflow-x-auto md:block">
          <table className="w-full min-w-[860px] border-collapse text-left">
            <thead className="sticky top-0 z-10 bg-surface/95 backdrop-blur">
              <tr className="border-b border-white/10">
                <th className="w-[28%] p-6 align-top">
                  <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">
                    Jämför tjänster
                  </div>
                  <div className="mt-2 text-sm text-foreground/70">
                    Pulse, Prime och Pilot — sida vid sida.
                  </div>
                </th>
                {columns.map((c) => (
                  <th
                    key={c.key}
                    className={`relative w-[24%] p-6 text-center align-top ${
                      c.highlighted ? "bg-mint/5" : ""
                    }`}
                  >
                    <div className="flex flex-col items-center">
                      <div
                        className={`inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] ring-1 ${accentBg(
                          c.accent,
                        )}`}
                      >
                        {c.eyebrow}
                      </div>
                      <div className={`mt-3 text-2xl font-semibold leading-tight text-${c.accent}`}>
                        {c.title}
                      </div>
                      <div className="mt-1 min-h-[2.5em] text-xs leading-snug text-foreground/70">
                        {c.desc}
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <Fragment key={g.id}>
                  <tr key={`${g.id}-h`} className="bg-white/[0.02]">
                    <td
                      colSpan={4}
                      className="px-6 py-3 text-[11px] font-semibold uppercase tracking-[0.22em] text-foreground/60"
                    >
                      {g.title}
                    </td>
                  </tr>
                  {g.rows.map((r) => (
                    <tr
                      key={`${g.id}-${r.label}`}
                      className="border-t border-white/5"
                    >
                      <td className="p-5 align-top">
                        <div className="text-sm font-medium text-foreground/90">
                          {r.label}
                        </div>
                        {r.hint && (
                          <div className="mt-0.5 text-xs text-muted-foreground">
                            {r.hint}
                          </div>
                        )}
                      </td>
                      {columns.map((c) => (
                        <td
                          key={c.key}
                          className={`p-5 text-center align-top ${
                            c.highlighted ? "bg-mint/5" : ""
                          }`}
                        >
                          <Value v={r[c.key]} accent={c.accent} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </Fragment>
              ))}
              <tr className="border-t border-white/10">
                <td className="p-6" />
                {columns.map((c) => (
                  <td
                    key={c.key}
                    className={`p-6 text-center ${c.highlighted ? "bg-mint/5" : ""}`}
                  >
                    <Link
                      to={c.href}
                      className={`inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-xs font-semibold transition-colors ${accentCta(
                        c.accent,
                      )}`}
                    >
                      {c.cta}
                    </Link>
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>

        {/* Mobile comparison — row-based with 3 mini columns */}
        <div className="md:hidden">
          {/* Sticky header with the three service columns */}
          <div className="sticky top-0 z-10 grid grid-cols-[1.2fr_1fr_1fr_1fr] gap-2 border-b border-white/10 bg-surface/95 px-3 py-3 backdrop-blur">
            <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
              Jämför
            </div>
            {columns.map((c) => (
              <div
                key={c.key}
                className={`flex flex-col items-center rounded-xl px-1 py-2 ${
                  c.highlighted ? "bg-mint/10" : ""
                }`}
              >
                <span
                  className={`inline-flex rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-[0.16em] ring-1 ${accentBg(
                    c.accent,
                  )}`}
                >
                  {c.eyebrow}
                </span>
                <span className={`mt-1.5 text-sm font-semibold leading-tight text-${c.accent}`}>
                  {c.title}
                </span>
              </div>
            ))}
          </div>

          {groups.map((g) => (
            <div key={g.id}>
              <div className="bg-white/[0.02] px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-foreground/60">
                {g.title}
              </div>
              {g.rows.map((r) => (
                <div
                  key={`m-${g.id}-${r.label}`}
                  className="grid grid-cols-[1.2fr_1fr_1fr_1fr] items-start gap-2 border-t border-white/5 px-3 py-3"
                >
                  <div className="min-w-0">
                    <div className="text-xs font-medium text-foreground/90">{r.label}</div>
                    {r.hint && (
                      <div className="mt-0.5 text-[10px] leading-snug text-muted-foreground">
                        {r.hint}
                      </div>
                    )}
                  </div>
                  {columns.map((c) => (
                    <div
                      key={`m-${g.id}-${r.label}-${c.key}`}
                      className={`flex min-h-[1.75rem] items-start justify-center text-center text-[11px] leading-snug text-foreground/85 ${
                        c.highlighted ? "bg-mint/5" : ""
                      }`}
                    >
                      {r[c.key] === true ? (
                        <Check className={`h-4 w-4 text-${c.accent}`} aria-label="Ja" />
                      ) : r[c.key] === false ? (
                        <Minus className="h-4 w-4 text-muted-foreground/40" aria-label="Nej" />
                      ) : (
                        <span>{r[c.key] as string}</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ))}

          {/* CTAs aligned across the same grid */}
          <div className="grid grid-cols-[1.2fr_1fr_1fr_1fr] gap-2 border-t border-white/10 px-3 py-4">
            <div />
            {columns.map((c) => (
              <Link
                key={`m-cta-${c.key}`}
                to={c.href}
                className={`inline-flex items-center justify-center rounded-full px-2 py-2 text-[11px] font-semibold ${accentCta(
                  c.accent,
                )}`}
              >
                {c.cta}
              </Link>
            ))}
          </div>
        </div>
      </div>

      <p className="mt-4 text-center text-xs text-muted-foreground">
        Osäker på vilken som passar? Använd{" "}
        <a href="#matcher" className="underline underline-offset-4 hover:text-foreground">
          tjänstematchningen
        </a>{" "}
        ovan — eller{" "}
        <Link to="/kontakt" className="underline underline-offset-4 hover:text-foreground">
          boka 30 min
        </Link>
        .
      </p>
    </Reveal>
  );
}
